import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Anchor, Paper, Group, Text, Badge, Stack } from '@mantine/core'
import { IconAlertCircle } from '@tabler/icons-react'
import { EmptyState, StatusBadge } from '@lighthouse/design'
import type { RunningView, StaleStatus } from '../../hooks/useEtlActivity'
import { elapsedSince, runningTimerText, staleHeadline } from '../../hooks/useEtlActivity'
import { NOT_MEASURED, errorKind, errorSummary, exactNumber, whenLabel } from '../../utils/format'
import type { ErrorKind } from '../../utils/format'

/**
 * Live Activity's banners. Every one of them is decided by the SERVED live-operations answer
 * (useEtlActivity's `live`), never by which hub events this browser happened to hear:
 * - ActiveJobBanner: non-stale work is in flight;
 * - StaleWorkBanner: only work the worker's own heartbeat rule presumes dead — a warning, not "running";
 * - LiveStatusUnavailable: the answer could not be read, which is not the same finding as idle;
 * - LiveStatusQuiet: checking, or nothing in flight.
 */

/** How many live entity runs the banner names before summarising the rest. */
const BANNER_ENTITY_LIMIT = 8

function plural(n: number, one: string, many: string): string {
  return `${exactNumber(n)} ${n === 1 ? one : many}`
}

/** "3 entity runs and 1 run" — naming only the counts that are not zero. */
function staleCounts(operations: number, runs: number): { text: string; many: boolean } {
  const parts: string[] = []
  if (operations > 0) parts.push(plural(operations, 'entity run', 'entity runs'))
  if (runs > 0) parts.push(plural(runs, 'run', 'runs'))
  if (parts.length === 0) return { text: 'No row', many: false }
  return { text: parts.join(' and '), many: parts.length > 1 || operations > 1 || runs > 1 }
}

const EXECUTION_HISTORY_STALE = '/execution-history?status=stale'

interface ActiveJobBannerProps {
  view: RunningView
}

export function ActiveJobBanner({ view }: ActiveJobBannerProps) {
  // Ticks the age forward between polls; the age itself is measured on the server's clock.
  const [now, setNow] = useState<number | null>(null)
  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(interval)
  }, [])

  const elapsed = elapsedSince(view.startedAt, view.observedAt, view.receivedAt, now)
  const listed = view.entities.slice(0, BANNER_ENTITY_LIMIT)
  const unlisted = view.entities.length - listed.length + view.entitiesNotListed
  const staleAlso = view.staleOperationCount > 0 || view.staleRunCount > 0

  return (
    <Paper
      shadow="md"
      p="lg"
      radius="md"
      data-state="running"
      role="status"
      style={{
        background: 'var(--gradient-accent)',
        border: '1px solid var(--color-border)',
        color: 'white',
      }}
    >
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="md" align="center">
            {/* Pulsing indicator */}
            <div
              style={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                background: 'var(--color-active)',
                boxShadow: '0 0 8px var(--color-active)',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
            <Text fw={700} size="lg" style={{ color: 'white' }}>
              ETL Job Running
            </Text>
          </Group>
          <Group gap="sm">
            {view.jobType && (
              <Badge color="white" variant="light" size="lg">
                {view.jobType}
              </Badge>
            )}
            {view.wasReset && (
              <Badge color="red" variant="filled" size="lg">
                Reset
              </Badge>
            )}
          </Group>
        </Group>

        <Group justify="space-between" align="center">
          <Text size="sm" style={{ color: 'rgba(255,255,255,0.85)' }}>
            {plural(view.liveOperationCount, 'entity run', 'entity runs')} in flight
            {' · '}
            {plural(view.liveRunCount, 'run', 'runs')} with a live heartbeat
          </Text>
          <Text
            size="sm"
            fw={600}
            style={{ color: 'rgba(255,255,255,0.9)' }}
            title={view.resumed
              ? "Since the earliest live run was FIRST started, on the server's clock. A resume keeps that start, so this includes the time before the resume."
              : "Since the earliest live run started, on the server's clock"}
          >
            {runningTimerText(elapsed, view.resumed)}
          </Text>
        </Group>

        {listed.length > 0 && (
          <Stack gap={4}>
            {listed.map((entity) => (
              <Group key={entity.entityRunId} gap="xs" wrap="nowrap" data-entity={entity.entityName}>
                <Anchor
                  component={Link}
                  to={`/etl-explorer/${encodeURIComponent(entity.entityRunId)}`}
                  size="sm"
                  fw={600}
                  style={{ color: 'white' }}
                  underline="always"
                >
                  {entity.entityName}
                </Anchor>
                {entity.module && (
                  <Text size="xs" style={{ color: 'rgba(255,255,255,0.75)' }}>{entity.module}</Text>
                )}
                <Text size="xs" style={{ color: 'rgba(255,255,255,0.85)' }} title="The newest chunk, as the worker recorded it">
                  {entity.phase ?? NOT_MEASURED}
                </Text>
                {entity.rowsThisAttempt != null && (
                  <Text size="xs" style={{ color: 'rgba(255,255,255,0.85)' }} title="From this session's live event stream">
                    {exactNumber(entity.rowsThisAttempt)} rows so far this attempt
                  </Text>
                )}
              </Group>
            ))}
            {unlisted > 0 && (
              <Text size="xs" style={{ color: 'rgba(255,255,255,0.75)' }}>
                and {plural(unlisted, 'more entity run', 'more entity runs')} not listed here
              </Text>
            )}
          </Stack>
        )}

        {staleAlso && (
          <Text size="xs" style={{ color: 'rgba(255,255,255,0.85)' }} data-note="stale-also">
            Also recorded as Running with no heartbeat for more than {view.staleRule.minutes} min:{' '}
            {staleCounts(view.staleOperationCount, view.staleRunCount).text}.{' '}
            <Anchor component={Link} to={EXECUTION_HISTORY_STALE} size="xs" style={{ color: 'white' }} underline="always">
              See them in Execution History
            </Anchor>
          </Text>
        )}
      </Stack>
    </Paper>
  )
}

/**
 * Only stale work: rows still say Running, but past the worker's heartbeat rule. Warning tone and a
 * different headline, so it can never be read as a job in flight — nothing closes such a row, so
 * treating it as running would pin this banner open for all time.
 */
export function StaleWorkBanner({ status }: { status: StaleStatus }) {
  const counts = staleCounts(status.staleOperationCount, status.staleRunCount)
  return (
    <Paper
      p="lg"
      radius="md"
      data-state="stale"
      data-tone="warning"
      role="status"
      style={{
        background: 'var(--status-warning-bg)',
        border: '1px solid var(--status-warning-border)',
      }}
    >
      <Stack gap="xs">
        <Group gap="sm" align="center">
          <StatusBadge status="warning" label="No heartbeat" />
          <Text fw={700} size="md" style={{ color: 'var(--status-warning-fg)' }}>
            {staleHeadline(status.staleRule)}
          </Text>
        </Group>
        <Text size="sm">
          {counts.text} {counts.many ? 'are' : 'is'} still recorded as Running, but nothing is in flight: by the
          worker&apos;s own rule {counts.many ? 'they are' : 'it is'} presumed dead, and nothing closes {counts.many ? 'them' : 'it'}.
          {!status.staleRule.published && ' The threshold is the code default — Automaton has not published its value.'}
        </Text>
        <Text size="xs" c="dimmed">
          Oldest last sign of life: {whenLabel(status.oldestStaleSince)}. {status.staleBasis}
        </Text>
        <Anchor component={Link} to={EXECUTION_HISTORY_STALE} size="sm">
          See them in Execution History
        </Anchor>
      </Stack>
    </Paper>
  )
}

/** What a failed live-operations read says, by kind. None of it means "nothing is running". */
function liveErrorCopy(kind: ErrorKind): { title: string; message: string } {
  const noVerdict = 'This page cannot say whether an ETL job is running.'
  switch (kind) {
    case 'unauthorized':
      return { title: 'Sign-in was not accepted', message: `The API did not accept this session. ${noVerdict}` }
    case 'forbidden':
      return {
        title: 'The API refused this request',
        message: 'Live operations are served to any signed-in user, so this is not a missing Lighthouse.Admin role; '
          + `something in front of the API refused it. ${noVerdict}`,
      }
    case 'network':
      return { title: 'Could not reach the API', message: `The request never got an answer. ${noVerdict}` }
    case 'server':
      return { title: 'The API failed while reading live operations', message: `It answered with a server error. ${noVerdict}` }
    case 'not-found':
      return { title: 'Live operations are not served here', message: `This API does not have the endpoint. ${noVerdict}` }
    default:
      return { title: 'Could not read what is running', message: noVerdict }
  }
}

export function LiveStatusUnavailable({ error }: { error: unknown }) {
  const copy = liveErrorCopy(errorKind(error))
  const said = errorSummary(error)
  return (
    <Paper p="md" withBorder data-state="unavailable" style={{ backgroundColor: 'var(--color-bg-surface)' }}>
      <div role="alert">
        <EmptyState
          icon={<IconAlertCircle size={28} stroke={1.5} color="var(--mantine-color-red-6)" />}
          title={copy.title}
          message={<>{copy.message}{said && <> The server says: {said}</>}</>}
        />
      </div>
    </Paper>
  )
}

/** Checking, or served and nothing in flight — visibly different from each other and from a failure. */
export function LiveStatusQuiet({ state, observedAt }: { state: 'loading' | 'idle'; observedAt?: string }) {
  if (state === 'loading') {
    return (
      <Text size="sm" c="dimmed" data-state="loading" aria-busy="true">
        Checking what the worker has recorded as running…
      </Text>
    )
  }
  return (
    <Text size="sm" c="dimmed" data-state="idle">
      Nothing is running: the worker records no entity run or run in flight
      {observedAt ? ` (as of ${whenLabel(observedAt)})` : ''}.
    </Text>
  )
}
